import clsx from 'clsx';
import { ChangeEvent, useState } from 'react';

import { IValueOptionsShape } from '~services/entity-definitions/interface';

export interface ISelectProps {
  name?: string;
  value: string | number;
  defaultValue?: string | number | boolean;
  label?: string;
  options?: IValueOptionsShape | Record<string, any>;
  className?: string;
  coerceNumber?: boolean;
  onChange: (value: string | number) => void;
  theme?: 'light' | 'dark';
  placeholder?: string;
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  fakeLevelOneOptgroup?: boolean;
}

export const Select = ({
  name,
  value,
  defaultValue,
  label,
  options,
  className,
  coerceNumber,
  onChange,
  placeholder,
  disabled,
  required,
  fakeLevelOneOptgroup,
}: ISelectProps) => {
  const [selected, setSelected] = useState(value === null ? defaultValue?.toString() : value);

  function handleChange(e: ChangeEvent<HTMLSelectElement>) {
    const val = coerceNumber ? parseInt(e.target.value, 10) : e.target.value;
    setSelected(val);
    onChange(val);
  }

  function Options({
    options,
    keyPrefix = 'group',
    fakeLevelOneOptgroup = false,
  }: {
    options: IValueOptionsShape | Record<string, any>;
    keyPrefix?: string;
    fakeLevelOneOptgroup?: boolean;
  }) {
    return (
      <>
        {Object.keys(options).map(key => {
          if (typeof options[key] === 'object') {
            if ('value' in options[key]) {
              if (typeof options[key].value === 'object') {
                // it is a IValueOptionsShape with childs
                if (fakeLevelOneOptgroup) {
                  return (
                    <>
                      <optgroup label={options[key].label} key={keyPrefix + key}></optgroup>
                      <Options key={key} options={options[key].value} />
                    </>
                  );
                }
                return (
                  <optgroup label={options[key].label} key={keyPrefix + key}>
                    <Options key={key} options={options[key].value} />
                  </optgroup>
                );
              }
              // it is a IValueOptionShape
              return (
                <option key={key} value={options[key].value}>
                  {options[key].label}
                </option>
              );
            }
            // it is an array with childs
            if (fakeLevelOneOptgroup) {
              return (
                <>
                  <optgroup label={key} key={keyPrefix + key}></optgroup>
                  <Options key={key} options={options[key]} />
                </>
              );
            }
            return (
              <optgroup label={key} key={keyPrefix + key}>
                <Options key={key} options={options[key]} />
              </optgroup>
            );
          }
          // it is a simple value
          return (
            <option key={key} value={options[key]}>
              {key}
            </option>
          );
        })}
      </>
    );
  }

  return (
    <div className="c-form-element c-form-element--type-select">
      <div className="o-input o-input--select">
        {label && (
          <label className="c-label" htmlFor={name}>
            {label}
          </label>
        )}
        <select
          className={clsx(
            {
              'c-input': true,
              'c-input--select': true,
            },
            className,
          )}
          id={name}
          value={selected}
          onChange={handleChange}
          disabled={disabled}
          required={required}
        >
          {placeholder && options && <option value="">{placeholder}</option>}
          <Options options={options ?? []} keyPrefix="topgroup" fakeLevelOneOptgroup={fakeLevelOneOptgroup} />
        </select>
      </div>
    </div>
  );
};

export default Select;
